import type { ApiOptions } from './ApiProvider';
import Api from './ApiProvider';
import type { PaginationSearchParams } from './types/common';
import { ORDER_BY_DIR_SEARCH_PARAM } from './types/common';
import type {
  RewardPool,
  RewardPoolDistributionPolicy,
  RewardPoolLeaderboardUser,
  RewardPoolSelfEntry,
  RewardPoolsStats,
  RewardPoolStatus,
  RewardPoolTag,
  RewardPoolTicketCurrency,
  SelfRewardPoolAccessPolicy,
  SelfRewardPoolsStats} from './types/rewardPools';
import {
  REWARD_POOL_STATUS
} from './types/rewardPools';

export const getRewardPoolsStats = (options?: ApiOptions) => {
  return Api.get('v2/karma/reward-pool/stats', options).json<RewardPoolsStats>();
};

export const getSelfRewardPoolsStats = () => {
  return Api.get('v2/karma/reward-pool/stats-me').json<SelfRewardPoolsStats>();
};

export const REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY = {
  NEWEST: 'newest',
  ENDING_SOON: 'ending-soon',
  OLDEST: 'oldest',
} as const;

export type RewardPoolsReadableSearchParamsOrderByName =
  keyof typeof REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY;

export type RewardPoolsReadableSearchParamsOrderBy =
  (typeof REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY)[RewardPoolsReadableSearchParamsOrderByName];

export type GetRewardPoolsSearchParams = {
  mode?: RewardPoolStatus;
  distributionPolicies?: RewardPoolDistributionPolicy[];
  tags?: RewardPoolTag[];
  recreation?: boolean;
  availableForMe?: boolean;
  orderBy?: RewardPoolsReadableSearchParamsOrderBy;
} & PaginationSearchParams;

export const DEFAULT_GET_REWARD_POOL_LIMIT = 12;

export const populateRewardPoolSearchParamsWithDefaults = (params?: GetRewardPoolsSearchParams) => {
  return {
    limit: DEFAULT_GET_REWARD_POOL_LIMIT,
    offset: 0,
    mode: REWARD_POOL_STATUS.ACTIVE,
    orderBy: REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.NEWEST,
    ...params,
  } satisfies GetRewardPoolsSearchParams;
};

export const normalizeGetRewardPoolSearchParams = (
  params: GetRewardPoolsSearchParams,
): ApiOptions['searchParams'] => {
  const { tags, orderBy, distributionPolicies, ...other } = params;

  const searchParams: ApiOptions['searchParams'] = other;

  if (tags?.length) {
    tags.forEach((tag, i) => {
      searchParams[`tags[${i}]`] = tag;
    });
  }

  if (distributionPolicies?.length) {
    distributionPolicies.forEach((policy, i) => {
      searchParams[`distribution_policies[${i}]`] = policy;
    });
  }

  if (orderBy) {
    const normalizedOrderBy =
      {
        [REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.NEWEST]: [
          'created_at',
          ORDER_BY_DIR_SEARCH_PARAM.DESC,
        ],
        [REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.OLDEST]: [
          'created_at',
          ORDER_BY_DIR_SEARCH_PARAM.ASC,
        ],
        [REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.ENDING_SOON]: [
          'end_date',
          ORDER_BY_DIR_SEARCH_PARAM.ASC,
        ],
      }[orderBy] || [];

    normalizedOrderBy.forEach((value, i) => {
      searchParams[`orderBy[${i}]`] = value;
    });
  }

  return searchParams;
};

export interface GetRewardPoolsData {
  pools: RewardPool[];
  myPoolIds: number[];
}

export const getRewardPools = (params: GetRewardPoolsSearchParams, options?: ApiOptions) => {
  const searchParams = normalizeGetRewardPoolSearchParams(
    populateRewardPoolSearchParamsWithDefaults(params),
  );

  const optionsWithDefaults: typeof options = {
    ...options,
    searchParams,
  };

  return Api.get('v2/karma/reward-pool', optionsWithDefaults).json<GetRewardPoolsData>();
};

interface RewardPoolSignProps {
  walletSign: {
    message: string;
    signature: string;
    pubKey: string;
  };
}

export interface JoinRewardPoolData extends RewardPoolSignProps {
  entryCurrency?: RewardPoolTicketCurrency;
  entryAmount?: number;
}

export const joinRewardPool = ({
  rewardPoolId,
  data,
}: {
  rewardPoolId: number;
  data: JoinRewardPoolData;
}) => {
  return Api.post(`v2/karma/reward-pool/${rewardPoolId}/join`, {
    json: {
      ...data,
    },
  }).json();
};

interface BuyEntryProps extends RewardPoolSignProps {
  rewardPoolId: number;
  entryCurrency: RewardPoolTicketCurrency;
  entryAmount: number;
}

export const rewardPoolBuyEntry = ({ rewardPoolId, ...rest }: BuyEntryProps) => {
  return Api.post(`v2/karma/reward-pool/${rewardPoolId}/buy-entry`, {
    json: {
      ...rest,
    },
  }).json();
};

type GetEntriesProps = {
  rewardPoolId: number;
};

export const getRewardPoolSelfEntries = ({ rewardPoolId }: GetEntriesProps) => {
  return Api.get(`v2/karma/reward-pool/entry/my`, {
    searchParams: { poolId: rewardPoolId },
  }).json<RewardPoolSelfEntry[]>();
};

export const getRewardPoolAccessPolicies = (
  rewardPoolId: number | string,
  options?: ApiOptions,
) => {
  return Api.get(`v2/karma/reward-pool/${rewardPoolId}/access-policies`, options).json<
    SelfRewardPoolAccessPolicy[]
  >();
};

export const getRewardPool = (rewardPoolId: number | string, options?: ApiOptions) => {
  return Api.get(`v2/karma/reward-pool/${rewardPoolId}`, options).json<RewardPool>();
};

export const GET_REWARD_POOL_LEADERBOARD_RAFFLE_MODE = {
  ALL: 'all',
  WINNERS: 'winner',
  LOSERS: 'notWinner',
} as const;

export type GetRewardPoolLeaderboardRaffleModeName =
  keyof typeof GET_REWARD_POOL_LEADERBOARD_RAFFLE_MODE;

export type GetRewardPoolLeaderboardRaffleMode =
  (typeof GET_REWARD_POOL_LEADERBOARD_RAFFLE_MODE)[GetRewardPoolLeaderboardRaffleModeName];

export type GetRewardPoolLeaderboardSearchParams = PaginationSearchParams & {
  raffleMode?: GetRewardPoolLeaderboardRaffleMode;
};

export const populateRewardPoolLeaderboardParamsWithDefaults = (
  params?: GetRewardPoolLeaderboardSearchParams,
) => {
  return {
    limit: 20,
    offset: 0,
    raffleMode: GET_REWARD_POOL_LEADERBOARD_RAFFLE_MODE.ALL,
    ...params,
  } satisfies GetRewardPoolLeaderboardSearchParams;
};

export interface RewardPoolLeaderboard {
  points_total: number;
  users: RewardPoolLeaderboardUser[];
}

/**
 * Default order:
 * FCFS and FCFS_V2 order by rank
 * PRO_RATA order by points
 * RAFFLE will only return data if pool status is COMPLETED; sort by points;
 * users who won will have points=1 therefore winners will be at the beginning
 */
export const getRewardPoolLeaderboard = async (
  rewardPoolId: string | number,
  params?: GetRewardPoolLeaderboardSearchParams,
): Promise<RewardPoolLeaderboard> => {
  const optionsWithDefaults: ApiOptions = {
    searchParams: populateRewardPoolLeaderboardParamsWithDefaults(params),
  };

  const data = await Api.get(
    `v2/karma/reward-pool/${rewardPoolId}/leaderboard`,
    optionsWithDefaults,
  ).json<RewardPoolLeaderboard | ''>();

  // NOTE: when no one participates backend returns empty string
  if (!data) {
    return {
      points_total: 0,
      users: [],
    };
  }

  return data;
};

export const getRewardPoolLeaderboardSelf = async (rewardPoolId: string | number) => {
  const data = await Api.get(`v2/karma/reward-pool/${rewardPoolId}/leaderboard-me`).json<
    RewardPoolLeaderboardUser | ''
  >();

  if (!data) {
    return null;
  }

  return data;
};
